import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@tagna/api-client';

// Basic user interface
export interface User {
  id: string;
  email: string;
  name: string;
  role?: 'user' | 'admin';
  avatar?: string;
}

// Auth context interface
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, confirmPassword: string, firstName: string, lastName: string) => Promise<void>;
  sendOtp: (email: string, type: 'registration' | 'login') => Promise<{ requestId: string }>;
  verifyOtp: (requestId: string, otpCode: string, email: string, fullName?: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user for development (currently unused)
// const mockUser: User = {
//   id: '1',
//   email: 'user@tagna.com',
//   name: 'Test User',
//   role: 'user',
//   avatar: '/avatar.jpg'
// };

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        if (authService.isAuthenticated()) {
          const userData = authService.getCurrentUserFromStorage();
          if (userData) {
            setUser({
              id: userData.id,
              email: userData.email,
              name: userData.fullName,
              role: 'user'
            });
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, _password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Use OTP-based login
      const otpResponse = await authService.sendOtp({ emailOrPhone: email, type: 'login' });
      if (!otpResponse.success) {
        throw new Error(otpResponse.message || 'Failed to send OTP');
      }
      // Note: The actual login completion happens in verifyOtp
      throw new Error('Please check your email for the OTP code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, _password: string, _confirmPassword: string, _firstName: string, _lastName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const otpResponse = await authService.sendOtp({ emailOrPhone: email, type: 'registration' });
      if (!otpResponse.success) {
        throw new Error(otpResponse.message || 'Failed to send registration OTP');
      }
      // Registration completion happens in verifyOtp
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtp = async (email: string, type: 'registration' | 'login'): Promise<{ requestId: string }> => {
    setError(null);

    try {
      const response = await authService.sendOtp({ emailOrPhone: email, type });
      if (!response.success) {
        throw new Error(response.message || 'Failed to send OTP');
      }
      return { requestId: response.data.requestId };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
      throw err;
    }
  };

  const verifyOtp = async (requestId: string, otpCode: string, email: string, fullName?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.verifyOtp({
        requestId,
        otpCode,
        emailOrPhone: email,
        fullName
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to verify OTP');
      }

      // Set user data
      setUser({
        id: response.data.user.id,
        email: response.data.user.email,
        name: response.data.user.fullName,
        role: 'user'
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OTP verification failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setError(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    sendOtp,
    verifyOtp,
    logout,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};